import { useHistory, useLocation } from 'react-router-dom';
import { Redirect, Route, Switch } from './router/react-router-v5-compat';
import { RoutesType, genRoutes } from './router.config';
import { useEffect } from 'react';
import { AccountType, accountState } from './page/my-account/account-state';
import { routerManager } from './router.manager';
import GlobalNotification from './component/common/snackbar';
import i18n from './i18n';
import { PermissionManager } from './router/permission.manager';
// import RbpPannel from './component/common/rbp-pannel';

interface PrivateRouteProps {
    route: RoutesType;
    isAuth?: boolean;
    [key: string]: unknown;
}

/**
 * 私有路由组件 - 需要登录才能访问
 */
function PrivateRoute({ route, ...rest }: PrivateRouteProps) {
    const location = useLocation();
    const history = useHistory();
    const currentPath = location.pathname;
    const isAuth = accountState.getFromLocalStore();
    const accountType = accountState.accountType;

    // 是否为新账号（需要修改密码）
    const userInfoIsNew = (accountState?.userInfo as Record<string, unknown>)?.is_edit_pass;
    const isNewAccount = userInfoIsNew !== undefined && userInfoIsNew === false;

    /**
     * 处理权限重定向和新账号密码修改逻辑
     */
    useEffect(() => {
        /**
         * 统一处理角色权限与新账号改密逻辑。
         */
        const guardRouteAccess = async () => {
            // 确保 accountType 已设置
            if (!accountType && accountState.userInfo?.account_type) {
                accountState.updateAccountType(accountState.userInfo.account_type as AccountType);
                return;
            }

            if (!accountType) {
                return;
            }

            // 检查权限并获取重定向路径
            const redirectPath = PermissionManager.getRedirectPath(currentPath, accountType);

            if (redirectPath && redirectPath !== currentPath) {
                history.replace(redirectPath);
                return;
            }

            // 新账号需要修改密码（仅对 STAFF 和 RESELLER）
            if (isNewAccount && (accountType === 'STAFF' || accountType === 'RESELLER')) {
                if (currentPath !== '/main/account') {
                    history.replace('/main/account');
                    GlobalNotification.warning(`${i18n.t('account_manager_please_modify_password_then_work')}`);
                    setTimeout(() => {
                        accountState.updatePanel(1);
                    }, 1000);
                }
                return;
            }
        };

        void guardRouteAccess();
    }, [currentPath, accountType, isNewAccount, history]);

    /**
     * 渲染路由组件或重定向到登录页
     */
    return (
        <Route
            {...rest}
            render={(props) => {
                if (!isAuth) {
                    return (
                        <Redirect
                            to={{
                                pathname: PermissionManager.getLoginPath(currentPath),
                            }}
                        />
                    );
                }

                // 确保 component 是有效的 React 组件
                const Component = route.component as React.ComponentType<{
                    routes?: RoutesType[];
                    [key: string]: unknown;
                }>;

                return <Component routes={route?.children} {...props} />;
            }}
        />
    );
}

/**
 * 基础路由组件
 */
const BasicRoute = () => {
    const routers = genRoutes();
    const location = useLocation();
    const history = useHistory();
    const isAuth = accountState.getFromLocalStore();

    /**
     * 过滤路由 - 根据用户权限
     */
    const filteredRouters = (() => {
        // 是否有Shell RBP Cloud使用权限
        const hasRBPCloudAccess = !!(accountState?.userInfo as Record<string, unknown>)?.auth_shell_1;
        
        if (hasRBPCloudAccess) {
            return routers;
        }

        // 移除 RBP Cloud 相关路由
        return routers.map(router => ({
            ...router,
            children: router?.children?.filter(child => child.path !== '/main/rbp-cloud') ?? []
        })) as RoutesType[];
    })();

    /**
     * 初始化路由管理器
     */
    useEffect(() => {
        routerManager.bindHistory(history);
    }, [history]);

    /**
     * 监听路由变化，更新认证状态
     */
    useEffect(() => {
        accountState.getFromLocalStore();
    }, [location.pathname, isAuth]);

    return (
        <Switch>
            {/* <Route path="/" component={RbpPannel} /> */}
            {
                filteredRouters.map((route: RoutesType) =>
                    route.private ?
                        <PrivateRoute
                            isAuth={isAuth}
                            key={route.id}
                            path={route.path}
                            exact={!route.children}
                            route={route}
                        />
                        : <Route
                            key={`${route.id}-${route.path}`}
                            path={route.path}
                            exact={!route.children}
                            render={(props) => {
                                const Component = route.component as React.ComponentType<{
                                    routes?: RoutesType[];
                                    [key: string]: unknown;
                                }>;
                                return <Component routes={route?.children} {...props} />
                            }}
                        />
                )
            }
            <Redirect
                from='/'
                to={'/staff'}
            />
            <Route path='*'>
                <div>404 Not Found</div>
            </Route>
        </Switch>
    );
}


export default BasicRoute;
